/**
 * Veltrona — core runtime
 * Small, dependency-free helpers and Web Components shared by every page.
 * Section-specific behaviour lives in its own asset and builds on these.
 */
(() => {
  'use strict';

  const root = document.documentElement;
  const motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');

  /* ----------------------------------------------------------------------
   * Utilities
   * -------------------------------------------------------------------- */
  const utils = {
    reducedMotion: () => motionQuery.matches,
    finePointer: () => finePointer.matches,

    debounce(fn, wait = 200) {
      let t;
      return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn.apply(null, args), wait);
      };
    },

    /** Runs fn at most once per animation frame. */
    rafThrottle(fn) {
      let queued = false;
      let lastArgs;
      return (...args) => {
        lastArgs = args;
        if (queued) return;
        queued = true;
        requestAnimationFrame(() => {
          queued = false;
          fn.apply(null, lastArgs);
        });
      };
    },

    fetchConfig(type = 'json') {
      return {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
      };
    },

    focusable(container) {
      return Array.from(
        container.querySelectorAll(
          'summary, a[href], button:enabled, [tabindex]:not([tabindex^="-"]), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe'
        )
      ).filter((el) => el.offsetParent !== null || el.getClientRects().length);
    },

    pauseMedia(scope = document) {
      scope.querySelectorAll('.js-youtube').forEach((v) => {
        v.contentWindow?.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
      });
      scope.querySelectorAll('.js-vimeo').forEach((v) => {
        v.contentWindow?.postMessage('{"method":"pause"}', '*');
      });
      scope.querySelectorAll('video').forEach((v) => v.pause());
    },

    /** Counted, so nested dialogs keep the page locked until the last one closes. */
    lockScroll(lock) {
      utils.locks = Math.max(0, (utils.locks || 0) + (lock ? 1 : -1));
      if (lock && utils.locks > 1) return;
      if (!lock && utils.locks > 0) return;
      if (lock) {
        const gap = window.innerWidth - root.clientWidth;
        root.style.setProperty('--scrollbar-gap', `${gap}px`);
        document.body.style.paddingRight = `${gap}px`;
        root.classList.add('scroll-locked');
        document.body.style.overflow = 'hidden';
      } else {
        document.body.style.paddingRight = '';
        document.body.style.overflow = '';
        root.classList.remove('scroll-locked');
      }
    },

    /** Emits a namespaced custom event on document. */
    emit(name, detail = {}) {
      document.dispatchEvent(new CustomEvent(`veltrona:${name}`, { detail }));
    },

    on(name, handler) {
      document.addEventListener(`veltrona:${name}`, handler);
      return () => document.removeEventListener(`veltrona:${name}`, handler);
    },

    formatMoney(cents, format = window.Veltrona?.moneyFormat || '${{amount}}') {
      if (typeof cents === 'string') cents = cents.replace('.', '');
      const value = Number(cents) / 100;
      const pattern = /\{\{\s*(\w+)\s*\}\}/;
      const fmt = (n, decimals, thousands = ',', dec = '.') => {
        const fixed = n.toFixed(decimals).split('.');
        fixed[0] = fixed[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
        return fixed.join(dec);
      };
      return format.replace(pattern, (_, key) => {
        switch (key) {
          case 'amount_no_decimals':
            return fmt(value, 0);
          case 'amount_with_comma_separator':
            return fmt(value, 2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':
            return fmt(value, 0, '.', ',');
          case 'amount_with_apostrophe_separator':
            return fmt(value, 2, "'", '.');
          case 'amount_with_space_separator':
            return fmt(value, 2, ' ', ',');
          default:
            return fmt(value, 2);
        }
      });
    },
  };

  /* ----------------------------------------------------------------------
   * Scroll reveal + word-by-word headings + image curtains
   * -------------------------------------------------------------------- */
  const Reveal = {
    observer: null,

    enabled() {
      return root.classList.contains('js-reveal') && !utils.reducedMotion();
    },

    splitHeading(el) {
      if (el.dataset.split === 'done') return;
      const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
      const nodes = [];
      while (walker.nextNode()) nodes.push(walker.currentNode);
      let i = 0;
      nodes.forEach((node) => {
        if (!node.textContent.trim()) return;
        const frag = document.createDocumentFragment();
        node.textContent.split(/(\s+)/).forEach((part) => {
          if (!part) return;
          if (/^\s+$/.test(part)) {
            frag.appendChild(document.createTextNode(part));
            return;
          }
          const word = document.createElement('span');
          word.className = 'split-word';
          const inner = document.createElement('span');
          inner.className = 'split-word__inner';
          inner.style.setProperty('--i', i++);
          inner.textContent = part;
          word.appendChild(inner);
          frag.appendChild(word);
        });
        node.parentNode.replaceChild(frag, node);
      });
      el.classList.add('split-heading');
      el.dataset.split = 'done';
    },

    init(scope = document) {
      const targets = scope.querySelectorAll('[data-reveal], [data-reveal-image]');
      if (!this.enabled()) {
        targets.forEach((el) => el.classList.add('is-revealed'));
        return;
      }

      if (root.dataset.splitHeadings === 'true') {
        scope.querySelectorAll('[data-reveal="split"]').forEach((el) => this.splitHeading(el));
      }

      // Stagger children of a reveal group.
      scope.querySelectorAll('[data-reveal-group]').forEach((group) => {
        const step = Number(group.dataset.revealGroup) || 80;
        group.querySelectorAll(':scope > [data-reveal], :scope > * > [data-reveal]').forEach((child, index) => {
          child.style.setProperty('--reveal-delay', `${Math.min(index, 10) * step}ms`);
        });
      });

      if (!this.observer) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (!entry.isIntersecting) return;
              entry.target.classList.add('is-revealed');
              this.observer.unobserve(entry.target);
            });
          },
          { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
        );
      }

      targets.forEach((el) => {
        if (el.classList.contains('is-revealed')) return;
        this.observer.observe(el);
      });
    },
  };

  /* ----------------------------------------------------------------------
   * Buttons: magnetic pull, liquid fill origin, click ripple
   * -------------------------------------------------------------------- */
  const Buttons = {
    init() {
      const style = root.dataset.buttonHover;

      document.addEventListener(
        'pointerover',
        (event) => {
          const btn = event.target.closest?.('.btn');
          if (!btn || btn.contains(event.relatedTarget)) return;
          if (style === 'fill') {
            const rect = btn.getBoundingClientRect();
            btn.style.setProperty('--x', `${event.clientX - rect.left}px`);
            btn.style.setProperty('--y', `${event.clientY - rect.top}px`);
          }
        },
        { passive: true }
      );

      document.addEventListener(
        'pointerout',
        (event) => {
          const btn = event.target.closest?.('.btn');
          if (!btn || btn.contains(event.relatedTarget)) return;
          if (style === 'fill') {
            const rect = btn.getBoundingClientRect();
            btn.style.setProperty('--x', `${event.clientX - rect.left}px`);
            btn.style.setProperty('--y', `${event.clientY - rect.top}px`);
          }
          if (btn.dataset.magnetic !== undefined) {
            btn.style.transform = '';
          }
        },
        { passive: true }
      );

      if (root.dataset.buttonMagnetic === 'true') {
        const move = utils.rafThrottle((event) => {
          if (!utils.finePointer() || utils.reducedMotion()) return;
          const btn = event.target.closest?.('[data-magnetic]');
          if (!btn) return;
          const rect = btn.getBoundingClientRect();
          const x = (event.clientX - rect.left - rect.width / 2) * 0.22;
          const y = (event.clientY - rect.top - rect.height / 2) * 0.32;
          btn.style.transform = `translate3d(${x.toFixed(1)}px, ${y.toFixed(1)}px, 0)`;
        });
        document.addEventListener('pointermove', move, { passive: true });
      }

      if (root.dataset.buttonRipple === 'true') {
        document.addEventListener(
          'pointerdown',
          (event) => {
            if (utils.reducedMotion()) return;
            const btn = event.target.closest?.('.btn');
            if (!btn) return;
            const rect = btn.getBoundingClientRect();
            const size = Math.max(rect.width, rect.height) * 2.2;
            const ripple = document.createElement('span');
            ripple.className = 'btn__ripple';
            ripple.style.width = ripple.style.height = `${size}px`;
            ripple.style.left = `${event.clientX - rect.left}px`;
            ripple.style.top = `${event.clientY - rect.top}px`;
            btn.appendChild(ripple);
            ripple.addEventListener('animationend', () => ripple.remove(), { once: true });
          },
          { passive: true }
        );
      }
    },
  };

  /* ----------------------------------------------------------------------
   * 3D tilt for cards (hover effect setting)
   * -------------------------------------------------------------------- */
  const Tilt = {
    init() {
      if (root.dataset.hover !== 'tilt') return;
      const move = utils.rafThrottle((event) => {
        if (!utils.finePointer() || utils.reducedMotion()) return;
        const card = event.target.closest?.('.hover-card');
        if (!card) return;
        const rect = card.getBoundingClientRect();
        const px = (event.clientX - rect.left) / rect.width - 0.5;
        const py = (event.clientY - rect.top) / rect.height - 0.5;
        card.style.setProperty('--rx', `${(px * 8).toFixed(2)}deg`);
        card.style.setProperty('--ry', `${(-py * 8).toFixed(2)}deg`);
      });
      document.addEventListener('pointermove', move, { passive: true });
      document.addEventListener(
        'pointerout',
        (event) => {
          const card = event.target.closest?.('.hover-card');
          if (card && !card.contains(event.relatedTarget)) {
            card.style.removeProperty('--rx');
            card.style.removeProperty('--ry');
          }
        },
        { passive: true }
      );
    },
  };

  /* ----------------------------------------------------------------------
   * Custom cursor
   * -------------------------------------------------------------------- */
  const Cursor = {
    init() {
      if (root.dataset.cursor !== 'true' || !utils.finePointer() || utils.reducedMotion()) return;
      const cursor = document.createElement('div');
      cursor.className = 'cursor is-hidden';
      cursor.setAttribute('aria-hidden', 'true');
      cursor.innerHTML = '<span class="cursor__ring"></span><span class="cursor__dot"></span>';
      document.body.appendChild(cursor);
      const dot = cursor.querySelector('.cursor__dot');
      const ring = cursor.querySelector('.cursor__ring');
      let x = 0;
      let y = 0;
      let rx = 0;
      let ry = 0;

      document.addEventListener(
        'pointermove',
        (e) => {
          x = e.clientX;
          y = e.clientY;
          dot.style.transform = `translate3d(${x}px, ${y}px, 0)`;
          cursor.classList.remove('is-hidden');
          const interactive = e.target.closest?.('a, button, [role="button"], label, summary, input, select, textarea');
          cursor.classList.toggle('is-hovering', Boolean(interactive));
        },
        { passive: true }
      );
      document.addEventListener('pointerleave', () => cursor.classList.add('is-hidden'));

      const loop = () => {
        rx += (x - rx) * 0.18;
        ry += (y - ry) * 0.18;
        ring.style.transform = `translate3d(${rx}px, ${ry}px, 0)`;
        requestAnimationFrame(loop);
      };
      loop();
    },
  };

  /* ----------------------------------------------------------------------
   * Page chrome: scroll progress fallback, back to top, loading bar
   * -------------------------------------------------------------------- */
  const PageChrome = {
    init() {
      const progress = document.querySelector('.scroll-progress');
      const toTop = document.querySelector('.back-to-top');
      const needsProgressFallback = progress && !CSS.supports('animation-timeline: scroll()');

      const onScroll = utils.rafThrottle(() => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const p = max > 0 ? Math.min(window.scrollY / max, 1) : 0;
        if (needsProgressFallback) progress.style.setProperty('--progress', p.toFixed(4));
        if (toTop) {
          toTop.style.setProperty('--progress', p.toFixed(4));
          toTop.classList.toggle('is-visible', window.scrollY > window.innerHeight * 0.8);
        }
        root.classList.toggle('is-scrolled', window.scrollY > 10);
      });

      window.addEventListener('scroll', onScroll, { passive: true });
      onScroll();

      toTop?.addEventListener('click', () => {
        window.scrollTo({ top: 0, behavior: utils.reducedMotion() ? 'auto' : 'smooth' });
        document.querySelector('#MainContent')?.focus({ preventScroll: true });
      });

      const loader = document.querySelector('.page-loader');
      if (loader) {
        document.addEventListener('click', (event) => {
          const link = event.target.closest?.('a[href]');
          if (!link || event.defaultPrevented || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
          if (link.target === '_blank' || link.hasAttribute('download')) return;
          const url = new URL(link.href, window.location.href);
          if (url.origin !== window.location.origin) return;
          if (url.pathname === window.location.pathname && url.hash) return;
          if (link.getAttribute('href').startsWith('#')) return;
          if (link.matches('[data-cart-trigger], [data-quick-view], [data-dialog-open], [data-no-loader]')) return;
          // Wait for every other click handler, so links handled in place never show the bar.
          setTimeout(() => {
            if (event.defaultPrevented) return;
            loader.classList.add('is-loading');
            clearTimeout(this.loaderTimer);
            this.loaderTimer = setTimeout(() => loader.classList.remove('is-loading'), 8000);
          }, 0);
        });
        window.addEventListener('pageshow', () => loader.classList.remove('is-loading'));
      }
    },
  };

  /* ----------------------------------------------------------------------
   * Web Components
   * -------------------------------------------------------------------- */

  /** <vt-dialog> wraps a native <dialog> for drawers and modals. */
  class VtDialog extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('dialog');
      if (!this.dialog || this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.dialog.addEventListener('cancel', (e) => {
        e.preventDefault();
        this.close();
      });
      this.dialog.addEventListener('click', (e) => {
        if (e.target.closest('[data-dialog-close]') || e.target.classList.contains('vt-dialog__scrim')) this.close();
      });
    }

    disconnectedCallback() {
      // Release the scroll lock if the dialog is removed while open (theme editor re-renders).
      if (this.dialog?.open) utils.lockScroll(false);
    }

    get isOpen() {
      return this.dialog?.open;
    }

    open(trigger) {
      if (!this.dialog || this.dialog.open || !this.isConnected) return;
      this.trigger = trigger || null;
      this.returnFocus = trigger || document.activeElement;
      this.dialog.showModal();
      utils.lockScroll(true);
      requestAnimationFrame(() => requestAnimationFrame(() => this.dialog.classList.add('is-open')));
      trigger?.setAttribute('aria-expanded', 'true');
      const focusTarget = this.dialog.querySelector('[autofocus]') || utils.focusable(this.dialog)[0];
      focusTarget?.focus({ preventScroll: true });
      this.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
    }

    close() {
      if (!this.dialog?.open) return;
      this.dialog.classList.remove('is-open');
      const panel = this.dialog.querySelector('.vt-dialog__panel');
      const finish = () => {
        this.dialog.close();
        utils.lockScroll(false);
        utils.pauseMedia(this.dialog);
        this.trigger?.setAttribute('aria-expanded', 'false');
        if (this.returnFocus?.isConnected) this.returnFocus.focus({ preventScroll: true });
        this.dispatchEvent(new CustomEvent('dialog:close', { bubbles: true }));
      };
      if (utils.reducedMotion() || !panel) return finish();
      let done = false;
      const once = () => {
        if (done) return;
        done = true;
        finish();
      };
      panel.addEventListener('transitionend', once, { once: true });
      setTimeout(once, 700);
    }

    toggle(trigger) {
      this.isOpen ? this.close() : this.open(trigger);
    }
  }

  /** Selects with [data-auto-submit] submit their form on change (localization forms). */
  document.addEventListener('change', (event) => {
    const select = event.target.closest?.('[data-auto-submit]');
    if (select?.form) select.form.requestSubmit ? select.form.requestSubmit() : select.form.submit();
  });

  /** [data-copy-link] copies a URL and announces it. */
  document.addEventListener('click', async (event) => {
    const btn = event.target.closest?.('[data-copy-link]');
    if (!btn) return;
    try {
      await navigator.clipboard.writeText(btn.dataset.copyLink);
      const live = document.getElementById('LiveRegion');
      if (live) live.textContent = window.Veltrona?.strings?.copied || '';
      btn.classList.add('is-copied');
      setTimeout(() => btn.classList.remove('is-copied'), 1800);
    } catch (e) {
      window.prompt('', btn.dataset.copyLink);
    }
  });

  /** Any element with [data-dialog-open="id"] opens that dialog. */
  document.addEventListener('click', (event) => {
    const trigger = event.target.closest?.('[data-dialog-open]');
    if (!trigger) return;
    const target = document.getElementById(trigger.dataset.dialogOpen);
    if (target && typeof target.open === 'function') {
      event.preventDefault();
      target.open(trigger);
    }
  });

  /** <vt-accordion> animates a <details> element open and closed. */
  class VtAccordion extends HTMLElement {
    connectedCallback() {
      this.details = this.querySelector('details');
      this.summary = this.details?.querySelector('summary');
      this.content = this.details?.querySelector('.accordion__content');
      if (!this.summary || !this.content || this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.summary.addEventListener('click', (e) => this.onClick(e));
    }

    onClick(event) {
      if (utils.reducedMotion()) return;
      event.preventDefault();
      const d = this.details;
      if (this.animation) this.animation.cancel();
      if (d.open) {
        const start = this.content.offsetHeight;
        this.animation = this.content.animate(
          { height: [`${start}px`, '0px'], opacity: [1, 0] },
          { duration: 350, easing: 'cubic-bezier(.65,0,.35,1)' }
        );
        this.animation.onfinish = () => {
          d.open = false;
          this.animation = null;
        };
      } else {
        if (this.dataset.group) {
          document
            .querySelectorAll(`vt-accordion[data-group="${this.dataset.group}"] details[open]`)
            .forEach((other) => other !== d && other.closest('vt-accordion').onClick(new Event('click')));
        }
        d.open = true;
        const end = this.content.offsetHeight;
        this.animation = this.content.animate(
          { height: ['0px', `${end}px`], opacity: [0, 1] },
          { duration: 450, easing: 'cubic-bezier(.16,1,.3,1)' }
        );
        this.animation.onfinish = () => (this.animation = null);
      }
    }
  }

  /** <vt-tabs> accessible tabs with a sliding indicator. */
  class VtTabs extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.tabs = Array.from(this.querySelectorAll(':scope [role="tab"]'));
      this.panels = this.tabs.map((t) => document.getElementById(t.getAttribute('aria-controls')));
      this.indicator = this.querySelector('.tabs__indicator');
      if (!this.tabs.length) return;
      this.dataset.ready = 'true';
      this.tabs.forEach((tab, i) => {
        tab.addEventListener('click', () => this.select(i, true));
        tab.addEventListener('keydown', (e) => this.onKey(e, i));
      });
      const initial = Math.max(0, this.tabs.findIndex((t) => t.getAttribute('aria-selected') === 'true'));
      this.select(initial, false);
      this.resizeObserver = new ResizeObserver(() => this.moveIndicator());
      this.resizeObserver.observe(this);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
    }

    onKey(e, i) {
      const last = this.tabs.length - 1;
      let next = null;
      if (e.key === 'ArrowRight') next = i === last ? 0 : i + 1;
      if (e.key === 'ArrowLeft') next = i === 0 ? last : i - 1;
      if (e.key === 'Home') next = 0;
      if (e.key === 'End') next = last;
      if (next === null) return;
      e.preventDefault();
      this.select(next, true);
      this.tabs[next].focus();
    }

    select(index, animate) {
      this.tabs.forEach((tab, i) => {
        const active = i === index;
        tab.setAttribute('aria-selected', active);
        tab.tabIndex = active ? 0 : -1;
        const panel = this.panels[i];
        if (!panel) return;
        panel.hidden = !active;
        if (active && animate) {
          panel.classList.remove('is-entering');
          void panel.offsetWidth;
          panel.classList.add('is-entering');
          Reveal.init(panel);
        }
      });
      this.current = index;
      this.moveIndicator();
      if (animate) this.dispatchEvent(new CustomEvent('tabs:change', { detail: { index }, bubbles: true }));
    }

    moveIndicator() {
      const tab = this.tabs[this.current];
      if (!this.indicator || !tab) return;
      this.indicator.style.setProperty('--x', `${tab.offsetLeft}px`);
      this.indicator.style.setProperty('--w', `${tab.offsetWidth}px`);
    }
  }

  /**
   * <vt-slider> scroll-snap carousel.
   * Markup: [data-slider-track] with slides, optional [data-slider-prev],
   * [data-slider-next], [data-slider-counter], [data-slider-progress], [data-slider-dots].
   * Attributes: data-autoplay="5" (seconds), data-loop.
   */
  class VtSlider extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.track = this.querySelector('[data-slider-track]');
      if (!this.track) return;
      this.dataset.ready = 'true';
      this.slides = Array.from(this.track.children).filter((el) => !el.hidden);
      this.prev = this.querySelector('[data-slider-prev]');
      this.next = this.querySelector('[data-slider-next]');
      this.counter = this.querySelector('[data-slider-counter]');
      this.progress = this.querySelector('[data-slider-progress]');
      this.dots = this.querySelector('[data-slider-dots]');
      this.loop = this.hasAttribute('data-loop');

      this.prev?.addEventListener('click', () => this.go(-1));
      this.next?.addEventListener('click', () => this.go(1));
      this.track.addEventListener('scroll', utils.rafThrottle(() => this.update()), { passive: true });
      this.buildDots();
      this.enableDrag();
      this.update();

      const autoplay = Number(this.dataset.autoplay);
      if (autoplay > 0 && !utils.reducedMotion()) {
        this.autoplayDelay = autoplay * 1000;
        this.play();
        this.addEventListener('pointerenter', () => this.pause());
        this.addEventListener('pointerleave', () => this.play());
        this.addEventListener('focusin', () => this.pause());
        this.addEventListener('focusout', () => this.play());
      }
      this.resizeObserver = new ResizeObserver(() => this.update());
      this.resizeObserver.observe(this.track);
    }

    disconnectedCallback() {
      this.pause();
      this.resizeObserver?.disconnect();
    }

    get step() {
      const first = this.slides[0];
      if (!first) return this.track.clientWidth;
      const second = this.slides[1];
      return second ? second.offsetLeft - first.offsetLeft : first.offsetWidth;
    }

    get index() {
      return Math.round(this.track.scrollLeft / Math.max(this.step, 1));
    }

    get pages() {
      const visible = Math.max(1, Math.round(this.track.clientWidth / Math.max(this.step, 1)));
      return Math.max(1, this.slides.length - visible + 1);
    }

    go(dir) {
      let target = this.index + dir;
      if (target >= this.pages) target = this.loop ? 0 : this.pages - 1;
      if (target < 0) target = this.loop ? this.pages - 1 : 0;
      this.goTo(target);
    }

    goTo(i) {
      const isRtl = getComputedStyle(this.track).direction === 'rtl';
      this.track.scrollTo({ left: (isRtl ? -1 : 1) * i * this.step, behavior: utils.reducedMotion() ? 'auto' : 'smooth' });
    }

    update() {
      const index = Math.min(this.index, this.pages - 1);
      const pages = this.pages;
      if (this.prev) this.prev.disabled = !this.loop && index <= 0;
      if (this.next) this.next.disabled = !this.loop && index >= pages - 1;
      if (this.counter) this.counter.textContent = `${String(index + 1).padStart(2, '0')} / ${String(pages).padStart(2, '0')}`;
      if (this.progress) this.progress.style.setProperty('--p', pages > 1 ? (index + 1) / pages : 1);
      this.dots?.querySelectorAll('button').forEach((d, i) => d.setAttribute('aria-current', i === index));
      this.slides.forEach((slide, i) => slide.classList.toggle('is-active', i === index));
      this.toggleAttribute('data-static', pages <= 1);
      if (index !== this.lastIndex) {
        this.lastIndex = index;
        this.dispatchEvent(new CustomEvent('slider:change', { detail: { index }, bubbles: true }));
      }
    }

    buildDots() {
      if (!this.dots) return;
      const label = this.dots.dataset.label || 'Go to slide';
      this.dots.innerHTML = '';
      for (let i = 0; i < this.pages; i++) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'slider-dot';
        dot.setAttribute('aria-label', `${label} ${i + 1}`);
        dot.addEventListener('click', () => this.goTo(i));
        this.dots.appendChild(dot);
      }
    }

    enableDrag() {
      let startX = 0;
      let startScroll = 0;
      let dragging = false;
      let moved = false;
      this.track.addEventListener('pointerdown', (e) => {
        if (e.pointerType !== 'mouse' || e.button !== 0) return;
        dragging = true;
        moved = false;
        startX = e.clientX;
        startScroll = this.track.scrollLeft;
      });
      window.addEventListener('pointermove', (e) => {
        if (!dragging) return;
        const dx = e.clientX - startX;
        if (Math.abs(dx) > 5 && !moved) {
          moved = true;
          this.track.classList.add('is-dragging');
          this.track.style.scrollSnapType = 'none';
        }
        if (moved) this.track.scrollLeft = startScroll - dx;
      });
      window.addEventListener('pointerup', () => {
        if (!dragging) return;
        dragging = false;
        if (!moved) return;
        this.track.classList.remove('is-dragging');
        this.track.style.scrollSnapType = '';
        this.goTo(this.index);
      });
      this.track.addEventListener(
        'click',
        (e) => {
          if (moved) {
            e.preventDefault();
            e.stopPropagation();
            moved = false;
          }
        },
        true
      );
    }

    play() {
      if (!this.autoplayDelay) return;
      this.pause();
      this.timer = setInterval(() => this.go(1), this.autoplayDelay);
    }

    pause() {
      clearInterval(this.timer);
    }
  }

  /** <vt-countdown data-end="2026-12-31T23:59"> counts down to a merchant-set date. */
  class VtCountdown extends HTMLElement {
    connectedCallback() {
      // Accept "2026-12-31 23:59" as well as "2026-12-31T23:59" (Safari only parses the second).
      const raw = (this.dataset.end || '').trim().replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T');
      const end = Date.parse(raw);
      if (Number.isNaN(end)) return;
      this.end = end;
      this.units = {
        days: this.querySelector('[data-unit="days"]'),
        hours: this.querySelector('[data-unit="hours"]'),
        minutes: this.querySelector('[data-unit="minutes"]'),
        seconds: this.querySelector('[data-unit="seconds"]'),
      };
      this.tick();
      this.timer = setInterval(() => this.tick(), 1000);
    }

    disconnectedCallback() {
      clearInterval(this.timer);
    }

    tick() {
      const diff = Math.max(0, this.end - Date.now());
      const values = {
        days: Math.floor(diff / 86400000),
        hours: Math.floor((diff / 3600000) % 24),
        minutes: Math.floor((diff / 60000) % 60),
        seconds: Math.floor((diff / 1000) % 60),
      };
      Object.entries(values).forEach(([k, v]) => {
        if (this.units[k]) this.units[k].textContent = String(v).padStart(2, '0');
      });
      if (diff === 0) {
        clearInterval(this.timer);
        this.classList.add('is-complete');
        if (this.dataset.hideOnComplete === 'true') (this.closest('[data-countdown-wrapper]') || this).setAttribute('hidden', '');
      }
    }
  }

  /**
   * <vt-video> lazy video: YouTube, YouTube Shorts, Vimeo or Shopify-hosted.
   * Only loads the player when requested or scrolled into view (autoplay).
   */
  class VtVideo extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.dataset.ready = 'true';
      this.button = this.querySelector('[data-video-play]');
      this.template = this.querySelector('template');
      this.button?.addEventListener('click', () => this.load(true));
      if (this.dataset.autoplay === 'true') {
        const io = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => {
              if (entry.isIntersecting) {
                this.load(false);
                io.disconnect();
              }
            });
          },
          { rootMargin: '200px' }
        );
        io.observe(this);
      }
    }

    load(focus) {
      if (this.loaded || !this.template) return;
      this.loaded = true;
      const content = this.template.content.firstElementChild.cloneNode(true);
      this.querySelector('[data-video-slot]')?.appendChild(content);
      this.classList.add('is-loaded');
      if (content.tagName === 'VIDEO') {
        const play = content.play?.();
        if (play) play.catch(() => {});
      }
      if (focus) content.focus?.({ preventScroll: true });
    }
  }

  /** <vt-marquee> duplicates its track so the loop is seamless. */
  class VtMarquee extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      const track = this.querySelector('.marquee__track');
      if (!track) return;
      this.dataset.ready = 'true';
      const clones = Number(this.dataset.clones) || 1;
      for (let i = 0; i < clones; i++) {
        const clone = track.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        clone.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
        this.appendChild(clone);
      }
    }
  }

  /** <vt-quantity> plus/minus control. */
  class VtQuantity extends HTMLElement {
    connectedCallback() {
      if (this.dataset.ready) return;
      this.input = this.querySelector('input');
      if (!this.input) return;
      this.dataset.ready = 'true';
      this.querySelectorAll('button[name]').forEach((btn) =>
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          const prev = this.input.value;
          btn.name === 'plus' ? this.input.stepUp() : this.input.stepDown();
          if (prev !== this.input.value) this.input.dispatchEvent(new Event('change', { bubbles: true }));
          this.syncButtons();
        })
      );
      this.input.addEventListener('change', () => this.syncButtons());
      this.syncButtons();
    }

    syncButtons() {
      const v = Number(this.input.value);
      const min = Number(this.input.min || 0);
      const max = this.input.max ? Number(this.input.max) : null;
      const minus = this.querySelector('button[name="minus"]');
      const plus = this.querySelector('button[name="plus"]');
      if (minus) minus.disabled = v <= min;
      if (plus) plus.disabled = max !== null && v >= max;
    }
  }

  /** <vt-fit-text> scales its text so a single line fills the container width. */
  class VtFitText extends HTMLElement {
    connectedCallback() {
      this.inner = this.firstElementChild;
      if (!this.inner) return;
      this.fit = this.fit.bind(this);
      this.resizeObserver = new ResizeObserver(() => this.fit());
      this.resizeObserver.observe(this);
      document.fonts?.ready.then(this.fit);
      document.fonts?.addEventListener?.('loadingdone', this.fit);
      window.addEventListener('load', this.fit, { once: true });
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      document.fonts?.removeEventListener?.('loadingdone', this.fit);
    }

    fit() {
      const max = Number(this.dataset.max) || 400;
      this.inner.style.fontSize = '100px';
      const ratio = this.clientWidth / Math.max(this.inner.scrollWidth, 1);
      this.inner.style.fontSize = `${Math.min(max, Math.floor(100 * ratio * 0.98))}px`;
    }
  }

  const define = (name, cls) => {
    if (!customElements.get(name)) customElements.define(name, cls);
  };
  define('vt-dialog', VtDialog);
  define('vt-accordion', VtAccordion);
  define('vt-tabs', VtTabs);
  define('vt-slider', VtSlider);
  define('vt-countdown', VtCountdown);
  define('vt-video', VtVideo);
  define('vt-marquee', VtMarquee);
  define('vt-quantity', VtQuantity);
  define('vt-fit-text', VtFitText);

  /* ----------------------------------------------------------------------
   * Scroll effects fallback for browsers without scroll-driven animations
   * [data-scroll-expand] receives --xp from 0 to 1 as it enters the viewport.
   * -------------------------------------------------------------------- */
  const ScrollFx = {
    init() {
      if (CSS.supports('animation-timeline: view()') || utils.reducedMotion()) return;
      const update = utils.rafThrottle(() => {
        const vh = window.innerHeight;
        document.querySelectorAll('[data-scroll-expand]').forEach((el) => {
          const r = el.getBoundingClientRect();
          const p = Math.min(1, Math.max(0, (vh - r.top) / (vh * 0.9)));
          el.style.setProperty('--xp', p.toFixed(3));
        });
      });
      window.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      document.addEventListener('shopify:section:load', update);
      update();
    },
  };

  /* ----------------------------------------------------------------------
   * details[data-open-desktop]: open on tablet/desktop, collapsible on mobile
   * -------------------------------------------------------------------- */
  const DesktopDetails = {
    query: window.matchMedia('(min-width: 750px)'),
    sync(scope = document) {
      scope.querySelectorAll('details[data-open-desktop]').forEach((d) => {
        d.open = this.query.matches;
      });
    },
    init() {
      this.sync();
      this.query.addEventListener('change', () => this.sync());
      document.addEventListener('click', (e) => {
        const summary = e.target.closest?.('details[data-open-desktop] > summary');
        if (summary && this.query.matches) e.preventDefault();
      });
    },
  };

  /* ----------------------------------------------------------------------
   * Theme editor support
   * -------------------------------------------------------------------- */
  const Editor = {
    init() {
      if (!window.Shopify?.designMode) return;
      document.addEventListener('shopify:section:load', (e) => {
        DesktopDetails.sync(e.target);
        Reveal.init(e.target);
        e.target.querySelectorAll('[data-reveal], [data-reveal-image]').forEach((el) => el.classList.add('is-revealed'));
      });
      document.addEventListener('shopify:block:select', (e) => {
        const slider = e.target.closest('vt-slider');
        if (slider) {
          const index = slider.slides?.indexOf(e.target.closest('[data-slider-track] > *'));
          if (index > -1) slider.goTo(index);
          slider.pause();
        }
      });
      document.addEventListener('shopify:block:deselect', (e) => e.target.closest('vt-slider')?.play());
      document.addEventListener('shopify:section:select', (e) => {
        e.target.querySelectorAll('[data-reveal], [data-reveal-image]').forEach((el) => el.classList.add('is-revealed'));
      });
    },
  };

  /* ----------------------------------------------------------------------
   * Boot
   * -------------------------------------------------------------------- */
  window.Veltrona = Object.assign(window.Veltrona || {}, {
    utils,
    Reveal,
    init(scope = document) {
      Reveal.init(scope);
    },
  });

  const boot = () => {
    Reveal.init();
    Buttons.init();
    Tilt.init();
    Cursor.init();
    PageChrome.init();
    DesktopDetails.init();
    ScrollFx.init();
    Editor.init();
    utils.emit('ready');
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Restore reveal state when returning through the back/forward cache.
  window.addEventListener('pageshow', (e) => {
    if (e.persisted) document.querySelectorAll('[data-reveal], [data-reveal-image]').forEach((el) => el.classList.add('is-revealed'));
  });
})();
